import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, Filter, X, ShoppingBag } from 'lucide-react';
import VirtualTryOn from '../components/VirtualTryOn';

const BACKEND_URL = '';

const CollectionPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showTryOn, setShowTryOn] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Scroll to top on page load and get category from URL
  useEffect(() => {
    window.scrollTo(0, 0);
    const categoryParam = searchParams.get('category');
    if (categoryParam && ['eyeglasses', 'sunglasses'].includes(categoryParam)) {
      setActiveCategory(categoryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [activeCategory]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const url = activeCategory === 'all' 
        ? `${BACKEND_URL}/api/products`
        : `${BACKEND_URL}/api/products?category=${activeCategory}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTryOn = (product) => {
    setSelectedProduct(product);
    setShowTryOn(true);
  };

  const categories = [
    { id: 'all', name: 'All Frames' },
    { id: 'eyeglasses', name: 'Eyeglasses' },
    { id: 'sunglasses', name: 'Sunglasses' }
  ];

  // Placeholder products if none exist in database
  const placeholderProducts = [
    {
      id: 'placeholder-1',
      name: 'Classic Round Frames',
      description: 'Timeless round frames perfect for any face shape',
      price: 199.99,
      sale_price: null,
      category: 'eyeglasses',
      brand: 'Designer Collection',
      images: ['https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=600&q=80'],
      stock: 10,
      has_virtual_tryon: true
    },
    {
      id: 'placeholder-2',
      name: 'Aviator Sunglasses',
      description: 'Classic aviator style with UV protection',
      price: 249.99,
      sale_price: 199.99,
      category: 'sunglasses',
      brand: 'Premium Line',
      images: ['https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600&q=80'],
      stock: 15,
      has_virtual_tryon: true
    },
    {
      id: 'placeholder-3',
      name: 'Modern Rectangle',
      description: 'Sleek rectangular frames for a professional look',
      price: 179.99,
      sale_price: null,
      category: 'eyeglasses',
      brand: 'Urban Style',
      images: ['https://images.unsplash.com/photo-1591076482161-42ce6da69f67?w=600&q=80'],
      stock: 8,
      has_virtual_tryon: true
    },
    {
      id: 'placeholder-4',
      name: 'Cat Eye Frames',
      description: 'Elegant cat eye design for a bold statement',
      price: 229.99,
      sale_price: null,
      category: 'eyeglasses',
      brand: 'Boutique Collection',
      images: ['https://images.unsplash.com/photo-1577803645773-f96470509666?w=600&q=80'],
      stock: 12,
      has_virtual_tryon: true
    },
    {
      id: 'placeholder-5',
      name: 'Wayfarer Style',
      description: 'Iconic wayfarer design with polarized lenses',
      price: 189.99,
      sale_price: 159.99,
      category: 'sunglasses',
      brand: 'Classic Line',
      images: ['https://images.unsplash.com/photo-1508296695146-257a814070b4?w=600&q=80'],
      stock: 20,
      has_virtual_tryon: true
    },
    {
      id: 'placeholder-6',
      name: 'Titanium Rimless',
      description: 'Ultra-light titanium frames, nearly invisible',
      price: 349.99,
      sale_price: null,
      category: 'eyeglasses',
      brand: 'Premium Collection',
      images: ['https://images.unsplash.com/photo-1614715838608-dd527c46231d?w=600&q=80'],
      stock: 5,
      has_virtual_tryon: true
    }
  ];

  const displayProducts = products.length > 0 ? products : placeholderProducts;
  const filteredProducts = activeCategory === 'all' 
    ? displayProducts 
    : displayProducts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white min-h-[50vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/6c31f705-b177-492e-87da-160782e583e7/images/0bfe9229f000ff5cf75d6e541c91169c38b43d79e77962351754ebd719a70343.png"
            alt="Eyewear collection"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Collection</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Browse our exclusive selection of designer frames and try them on virtually
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="bg-white border-b sticky top-20 z-40">
        <div className="container mx-auto px-4 py-3 md:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700 text-sm md:text-base">Filter:</span>
            </div>
            <div className="flex flex-wrap gap-2 overflow-x-auto pb-1">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full transition-all text-sm whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="text-xs md:text-sm text-gray-500">
              {filteredProducts.length} products
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-medium text-gray-600">No products found</h3>
              <p className="text-gray-500 mt-2">Check back soon for new arrivals!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {filteredProducts.map(product => (
                <div 
                  key={product.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow group"
                  data-testid={`product-card-${product.id}`}
                >
                  {/* Product Image - Clickable */}
                  <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-gray-100">
                    <img 
                      src={product.images?.[0] || 'https://via.placeholder.com/400x400?text=No+Image'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {product.sale_price && (
                      <div className="absolute top-3 left-3 md:top-4 md:left-4 bg-red-600 text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                        SALE
                      </div>
                    )}
                    {product.has_virtual_tryon && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleTryOn(product);
                        }}
                        className="absolute bottom-3 right-3 md:bottom-4 md:right-4 bg-white/90 backdrop-blur-sm text-gray-900 px-3 md:px-4 py-2 rounded-lg font-medium flex items-center gap-1 md:gap-2 hover:bg-white transition-colors shadow-md text-sm"
                        data-testid={`tryon-btn-${product.id}`}
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">Virtual Try-On</span>
                        <span className="sm:hidden">Try On</span>
                      </button>
                    )}
                  </Link>

                  {/* Product Info - Clickable */}
                  <Link to={`/product/${product.id}`} className="block p-4 md:p-6">
                    {product.brand && (
                      <p className="text-sm text-red-600 font-medium mb-1">{product.brand}</p>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {product.sale_price ? (
                          <>
                            <span className="text-2xl font-bold text-red-600">${product.sale_price}</span>
                            <span className="text-lg text-gray-400 line-through">${product.price}</span>
                          </>
                        ) : (
                          <span className="text-2xl font-bold text-gray-900">${product.price}</span>
                        )}
                      </div>
                      <span className={`text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                      </span>
                    </div>
                  </Link>

                  {/* Buttons - Not inside Link */}
                  <div className="px-6 pb-6 flex gap-2">
                    <button
                      onClick={() => handleTryOn(product)}
                      className="flex-1 bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Try On
                    </button>
                    <Link
                      to={`/product/${product.id}`}
                      className="flex-1 border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:border-red-600 hover:text-red-600 transition-colors text-center"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Virtual Try-On Modal */}
      <VirtualTryOn 
        isOpen={showTryOn} 
        onClose={() => setShowTryOn(false)} 
      />

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Can't Find What You're Looking For?</h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Visit our store to see our full collection of over 100+ designer brands
          </p>
          <Link
            to="/contact#contact-form"
            className="inline-block bg-red-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-red-700 transition-colors text-lg"
          >
            Book a Store Visit
          </Link>
        </div>
      </section>
    </div>
  );
};

// Virtual Try-On Component
export default CollectionPage;
